import { useInfiniteQuery } from '@tanstack/react-query';
import { Button, Dialog, DialogBody, DialogFooter, Spinner } from '@blueprintjs/core';
import { fetchExcluded, type AssignmentRequest, type ExcludedLead, type ExclusionKind } from './api';
import { leadImportKeys } from './keys';
import { queryPolicies } from '../../utils/queryPolicies';
import { ErrorNote, REASON_LABEL, fmtDay, fmtInt } from './ui';

const PAGE = 200;

const TITLE: Record<ExclusionKind, string> = {
  not_assignable: 'Not assignable',
  repeat: 'Skipped repeats',
  prior_rep: 'A rep already had these',
};

const INTRO: Record<ExclusionKind, string> = {
  not_assignable: 'These leads match the filters but can never be given out, whatever the repeat rule.',
  repeat: 'The repeat rule leaves these out because the same phone came in another import.',
  prior_rep: 'The repeat rule leaves these out because a rep had the same phone in another import.',
};

/** Why one lead was left out, in words. */
function why(kind: ExclusionKind, l: ExcludedLead): string {
  if (kind === 'not_assignable') return l.reasons.map((r) => REASON_LABEL[r] ?? r).join(', ');
  const names = (l.skipped_because ?? []).join(', ');
  return kind === 'repeat' ? (names ? `Also from ${names}` : 'Seen in another import') : (names ? `Had by ${names}` : 'Had by a rep');
}

/** The leads one preview line left out, worked out by the server with the preview's own request. */
export default function ExcludedLeadsDialog({ request, kind, onClose }: {
  request: AssignmentRequest; kind: ExclusionKind; onClose: () => void;
}) {
  const q = useInfiniteQuery({
    queryKey: leadImportKeys.excluded(request, kind),
    queryFn: ({ pageParam }) => fetchExcluded(request, kind, PAGE, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.rows.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
    ...queryPolicies.recordDetail,
  });
  const rows = (q.data?.pages ?? []).flatMap((p) => p.rows);
  const total = q.data?.pages[0]?.total ?? 0;

  return (
    <Dialog isOpen onClose={onClose} title={TITLE[kind]} style={{ width: 'min(980px, 100vw)' }}>
      <DialogBody>
        {q.isLoading && <Spinner size={20} />}
        <ErrorNote error={q.error} />
        {q.data && (
          <div className="li-stack">
            <p className="li-vrun__summary">{fmtInt(total)} {total === 1 ? 'lead' : 'leads'}. {INTRO[kind]}</p>
            <div className="li-tablewrap" style={{ maxHeight: '60vh' }}>
              <table className="li-table li-table--compact">
                <thead>
                  <tr><th>Lead</th><th>Phone</th><th>Why</th><th>Rep now</th><th>Merchant</th><th>Import</th></tr>
                </thead>
                <tbody>
                  {rows.map((l) => {
                    const name = [l.first_name, l.last_name].filter(Boolean).join(' ');
                    return (
                      <tr key={l.id}>
                        <td>
                          <b>{l.business_name || name || `Lead ${l.id}`}</b>
                          {l.business_name && name && <div className="li-muted">{name}</div>}
                        </td>
                        <td>{l.phone}</td>
                        <td>{why(kind, l)}</td>
                        <td>{l.current_rep_name ?? <span className="li-muted">-</span>}</td>
                        <td>{l.merchant_label ?? <span className="li-muted">-</span>}</td>
                        <td>
                          {l.file_name ?? `Import ${l.batch}`}
                          <div className="li-muted">{fmtDay(l.batch_date)}, row {fmtInt(l.origin_row)}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {q.hasNextPage && (
              <button type="button" className="li-btn li-btn--link" disabled={q.isFetchingNextPage} onClick={() => q.fetchNextPage()}>
                {q.isFetchingNextPage ? 'Loading…' : `Show more (${fmtInt(rows.length)} of ${fmtInt(total)})`}
              </button>
            )}
          </div>
        )}
      </DialogBody>
      <DialogFooter actions={<Button onClick={onClose}>Close</Button>} />
    </Dialog>
  );
}
